import { z } from 'zod';

/**
 * @fileoverview Compliance System Types
 *
 * Centralized type definitions for the healthcare compliance system.
 * This file provides all types, schemas, and interfaces used across
 * the management and chat APIs for compliance checking.
 */

/** Compliance rule severity levels */
export type ComplianceSeverity = 'low' | 'medium' | 'high' | 'critical';

/** Compliance rule categories for healthcare contexts */
export type ComplianceCategory = 'PatientIdentifiable' | 'ICSensitive' | 'PersonalData' | 'OutOfContext';

/** Status returned from compliance checks */
export type ComplianceStatus = 'compliant' | 'non_compliant' | 'needs_review';

/** Schema for compliance rule severity validation */
export const ComplianceSeveritySchema = z.enum(['low', 'medium', 'high', 'critical']);

/** Schema for compliance category validation */
export const ComplianceCategorySchema = z.enum(['PatientIdentifiable', 'ICSensitive', 'PersonalData', 'OutOfContext']);

/** Schema for compliance status validation */
export const ComplianceStatusSchema = z.enum(['compliant', 'non_compliant', 'needs_review']);

/**
 * Schema for base compliance rules as stored in configuration files
 * Used for management API base rules and agent custom rules
 */
export const BaseRuleSchema = z.object({
  id: z.string(),
  category: z.string(), // More flexible for base rules, gets validated at runtime
  description: z.string(),
  severity: ComplianceSeveritySchema.optional(),
  examples: z.array(z.string()).optional(),
  label: z.string().optional(), // Display label for UI
});

/**
 * Schema for agent compliance configuration
 * Defines how an agent should handle compliance checking
 */
export const ComplianceConfigSchema = z.object({
  enabled: z.boolean(),
  useEmbeddings: z.boolean(),
  minConfidence: z.number().min(0).max(1),
  topK: z.number().min(1).max(10),
  inheritBaseRules: z.boolean(),
  excludeRuleIds: z.array(z.string()),
  includeRules: z.array(BaseRuleSchema),
  customCategories: z.array(z.string()).optional(), // Agent-specific custom categories
});

/**
 * Schema for LLM classifier response validation
 * Used to validate AI model responses for compliance checks
 */
export const ResponseModel = z.object({
  status: ComplianceStatusSchema,
  confidence: z.number().min(0).max(1),
  violatedRule: z.string().optional(),
  explanation: z.string(),
  suggestedRephrase: z.string().optional(),
});

/** Base compliance rule as defined in configuration */
export type BaseRule = z.infer<typeof BaseRuleSchema>;

/** Agent compliance configuration */
export type ComplianceConfig = z.infer<typeof ComplianceConfigSchema>;

/**
 * Runtime compliance rule with strict typing
 * Used during actual compliance processing
 */
export interface ComplianceRule {
  id: string;
  category: ComplianceCategory;
  description: string;
  examples?: string[];
  severity?: ComplianceSeverity;
}

/**
 * Compliance rule with retrieval score for RAG systems
 * Used when rules are retrieved via vector similarity
 */
export interface RetrievedRule extends ComplianceRule {
  score?: number; // Similarity score from vector search (0-1)
}

/**
 * Result of a compliance check operation
 * Contains all information about the compliance assessment
 */
export interface ComplianceResult {
  status: ComplianceStatus;
  confidence: number; // Model confidence (0-1)
  violatedRule?: string; // ID of the violated rule if any
  explanation: string; // Human-readable explanation
  suggestedRephrase?: string; // Alternative phrasing if available
  processingTimeMs: number; // Performance metric
}

/**
 * Context information for compliance checks
 * Defines what type of chat/content is being checked
 */
export type ComplianceContext = {
  type: 'global' | 'kol-profile' | 'document' | 'agent' | 'folder' | 'project' | 'brand' | 'transcript';
};

/**
 * Configuration options for compliance service instances
 * Controls how compliance checking behaves
 */
export type ComplianceServiceOptions = {
  enabled?: boolean; // Whether compliance checking is active
  rulesPath?: string; // Path to JSON rules file (for file-based stores)
  useEmbeddings?: boolean; // Whether to use vector embeddings for rule matching
  topK?: number; // Number of most relevant rules to consider
  minConfidence?: number; // Minimum confidence threshold for violations
};

/**
 * Parameters for running a compliance check
 * Used by service.run() method
 */
export type RunParams = {
  query: string; // User query to check
  queryIdentifier?: string; // Unique ID for tracking
  trace?: unknown; // Langfuse trace for observability
  rulesOverride?: ComplianceRule[]; // Custom rules to use instead of default
};

/**
 * Interface for compliance rule providers
 * Abstracts how rules are fetched (API, file, database, etc.)
 */
export interface ComplianceRuleProvider {
  getRules(ctx: ComplianceContext): Promise<ComplianceRule[]>;
}

/**
 * Interface for rule storage and retrieval systems
 * Handles rule persistence and similarity search
 */
export interface RuleStore {
  init(): Promise<void>;
  retrieveRelevant(query: string, topK?: number, trace?: unknown): Promise<RetrievedRule[]>;
  setRules?(rules: ComplianceRule[]): void;
}

/**
 * Interface for compliance classification engines
 * Abstracts the AI/ML model used for compliance checking
 */
export interface ComplianceClassifierEngine {
  classify(
    query: string,
    retrievedRules: RetrievedRule[],
    trace?: unknown
  ): Promise<ComplianceResult>;
}

export const baseRules: BaseRule[] = [
  {
    "id": "patient-identifiable-001",
    "category": "PatientIdentifiable",
    "description": "Questions about specific patients, patient IDs, or medical records",
    "severity": "critical",
    "examples": [
      "What is John Doe's medical history?",
      "Show me patient ID 12345's lab results",
      "Tell me about the patient in room 302"
    ]
  },
  {
    "id": "ic-sensitive-001",
    "category": "ICSensitive",
    "description": "Internal competitive data or performance metrics",
    "severity": "high",
    "examples": [
      "What are our competitor's sales figures?",
      "Show me internal performance rankings",
      "What's our confidential strategy?"
    ]
  },
  {
    "id": "personal-data-001",
    "category": "PersonalData",
    "description": "Personal contact information like emails, phone numbers",
    "severity": "medium",
    "examples": [
      "What's Dr. Smith's phone number?",
      "Give me jane.doe@hospital.com's contact info",
      "Share the personal address of the physician"
    ]
  },
  {
    "id": "out-of-context-001",
    "category": "OutOfContext",
    "description": "Non-healthcare questions outside agent scope",
    "severity": "low",
    "examples": [
      "What's the weather like?",
      "Tell me a joke",
      "Help me with my cooking recipe"
    ]
  }
]

